import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { authActions } from "../slices/auth";
import { getErrorMessage } from "../../utils/errorUtils";
import { UserLoginDto } from "../../shared/dtos/auth/UserLoginDto";
import { PasswordResetRequestDto } from "../../shared/dtos/auth/PasswordResetRequestDto";
import { UserAuthResponse } from "../../shared/dtos/auth/UserAuthResponse";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { saveUser, saveToken, clearAuthStorage } from "../../lib/storage";

export const loginUser =
  (values: UserLoginDto) => async (dispatch: AppDispatch) => {
    dispatch(authActions.setLoadingUserInfo());

    const loginPayload = {
      email: values.email.trim(),
      password: values.password,
    };

    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<UserAuthResponse>
      >("/auth/users", loginPayload, { headers: { requiresAuth: false } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        const { userData, token } = data.data;

        saveUser(userData);
        saveToken(token);

        dispatch(authActions.userLoginSuccess(userData));
        toast.success(data.message || "Login Successful");
      }
    } catch (error) {
      dispatch(authActions.setErrorLoadingUserInfo(getErrorMessage(error)));
    }
  };

export const logoutUser = () => (dispatch: AppDispatch) => {
  dispatch(authActions.userLogout());
  clearAuthStorage();
  toast.success("Logout Successful!");
};

export const verifyUserEmail =
  (token: string) => async (dispatch: AppDispatch) => {
    dispatch(authActions.setLoadingUserVerify());
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<void>>(
        `/auth/verify-email/${token}`,
        { headers: { requiresAuth: false } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        dispatch(authActions.verifyUserEmailSuccess());
        toast.success(data.message || "Email verified! You can now log in.");
      }
    } catch (error) {
      dispatch(authActions.setErrorUserVerify(getErrorMessage(error)));
    }
  };

export const resendVerificationEmail =
  (values: { email: string }) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/auth/resend-verification-email",
        values,
        { headers: { requiresAuth: false } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "New link sent to your email.");
        dispatch(
          authActions.setServerResponseStatus({
            operation: "resend-verification",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

export const forgotPassword =
  (values: { email: string }) => async (dispatch: AppDispatch) => {
    dispatch(authActions.setLoadingForgotPassword());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/auth/forgot-password",
        values,
        { headers: { requiresAuth: false } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Reset link sent.");
        dispatch(authActions.forgotPasswordSuccess());
        dispatch(
          authActions.setServerResponseStatus({
            operation: "forgot-password",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(authActions.setErrorForgotPassword(getErrorMessage(error)));
    }
  };

export const resetUserPassword =
  (payload: PasswordResetRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(authActions.setLoadingResetPassword());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/auth/reset-password",
        null,
        {
          headers: { requiresAuth: false },
          params: { token: payload.token, newPassword: payload.newPassword },
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Password reset successfully!");
        dispatch(authActions.resetPasswordSuccess());
        dispatch(
          authActions.setServerResponseStatus({
            operation: "reset-password",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(authActions.setErrorResetPassword(message));
    }
  };

export const resetAuthServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(authActions.setServerResponseStatus(null));
};

export const clearVerifyError = () => (dispatch: AppDispatch) => {
  dispatch(authActions.setErrorUserVerify(null));
};

export const clearUserInfoError = () => (dispatch: AppDispatch) => {
  dispatch(authActions.setErrorLoadingUserInfo(null));
};
